import type { VlmAsk, VlmLoad, VlmReply } from "./vlm.ts";

/**
 * An air the customer hopes the piece has, beyond anything its shape is measured for (a
 * gentle roundness, a look of something good to eat): a vision-language model looks at
 * photos of the piece and says how strongly it comes across (see vlm.ts). It is shown beside
 * the judgement, never counted in it, since it is a matter of how one sees it; the customer
 * says what they make of it on the delivery slip.
 */
export interface Atmosphere {
  /** Short name, as in "雰囲気: やさしい丸み". */
  label: string;
  /**
   * The customer's word on the delivery slip as the air comes across somewhat, then
   * strongly: after their word on the wish, if they have one.
   */
  thanks: [string, string];
  /**
   * The customer's word instead, as the air comes across strongly though the shape is not
   * what they had in mind: not what they pictured, but they have taken to it all the same.
   */
  charmed: string;
  /**
   * Things with the air that the model knows well by sight, in English (which it reads
   * best): how much it says the piece looks like them is how strongly the air comes
   * across. The model cannot be asked about the air itself: a small one answers "how
   * delicious does it look?" from the words alone, whatever it is shown.
   */
  looksLike: string[];
}

/** How strongly the air comes across, as the result says it: hardly, somewhat, strongly. */
const LEVELS = ["あまり感じられない", "少し感じられる", "かなり感じられる"];
/**
 * The least score the customer's variant is taken to be above the untouched clay by: the
 * model's own wavering is about this much, and would otherwise fill the gauge.
 */
const MIN_SPAN = 0.5;

export interface AtmosphereRating {
  /** How strongly, 0–1: 0 as the untouched clay looks, 1 as the customer's variant does. */
  strength: number;
  /** The level that strength is at, a third of the way each: 0 hardly, 1 somewhat, 2 strongly. */
  level: number;
  /** The level in words. */
  text: string;
}

let worker: Worker | null = null;
let asks = 0;
const waiting = new Map<number, { done(shares: number[]): void; fail(e: Error): void }>();
let onLoading: ((share: number) => void) | null = null;
/** How much of the model has come so far: 1 once it has answered. */
let loadedShare = 0;

function vlm(): Worker {
  if (worker) return worker;
  const w = new Worker(new URL("vlm.js", import.meta.url), { type: "module" });
  w.onmessage = (e: MessageEvent<VlmReply>) => {
    const reply = e.data;
    if (reply.kind === "loading") {
      loadedShare = reply.total ? reply.loaded / reply.total : 0;
      onLoading?.(loadedShare);
      return;
    }
    const ask = waiting.get(reply.id);
    waiting.delete(reply.id);
    if (reply.kind === "answer") {
      loadedShare = 1;
      ask?.done(reply.shares);
    } else ask?.fail(new Error(reply.message));
  };
  // The worker itself could not start (its script, say): nothing asked will be answered.
  w.onerror = () => {
    for (const ask of waiting.values()) ask.fail(new Error("the VLM worker failed"));
    waiting.clear();
    worker = null;
  };
  return worker = w;
}

/**
 * Asks the model a question about `image` (see vlm.ts): how much each of `answers` is its
 * own, said first or after `prefix` (the start of its reply, written for it). While the model
 * loads, `loading` hears how much of it has come.
 */
export function ask(
  image: ImageData,
  question: string,
  answers: string[],
  loading: (share: number) => void,
  prefix?: string,
): Promise<number[]> {
  onLoading = loading;
  const a: VlmAsk = { kind: "ask", id: ++asks, image, question, answers, prefix };
  return new Promise((done, fail) => {
    waiting.set(a.id, { done, fail });
    vlm().postMessage(a);
  });
}

/**
 * Has `loading` hear how much of the model has come, from now on while it loads (as `ask`
 * does), and says how much has come so far.
 */
export function followLoading(loading: (share: number) => void): number {
  onLoading = loading;
  return loadedShare;
}

let able: Promise<boolean> | null = null;

/** Whether this browser can run the model at all (it needs WebGPU). */
export function canLook(): Promise<boolean> {
  able ??= (async () => {
    try {
      return !!await navigator.gpu?.requestAdapter();
    } catch {
      return false;
    }
  })();
  return able;
}

/** Starts loading the model before it is asked anything, so that it is ready sooner. */
export async function prepareToLook(): Promise<void> {
  if (await canLook()) vlm().postMessage({ kind: "load" } satisfies VlmLoad);
}

/**
 * How much the photos look like what has the air: the log-odds of the model's "Yes" to each
 * of them, on average.
 */
async function scoreOf(
  photos: ImageData[],
  atmosphere: Atmosphere,
  loading: (share: number) => void,
): Promise<number> {
  const scores = await Promise.all(
    photos.flatMap((photo) =>
      atmosphere.looksLike.map(async (thing) => {
        const [yes, no] = await ask(
          photo,
          `Does this look like ${thing}? Answer yes or no.`,
          ["Yes", "No"],
          loading,
        );
        return Math.log(yes / no);
      })
    ),
  );
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

/** Each request's scores of the untouched clay and of the customer's variant, once found. */
const anchors = new WeakMap<Atmosphere, Promise<[number, number]>>();

/**
 * Where those scores are kept from one visit to the next: they come out the same each time,
 * and finding them takes twice as long as looking at the piece, the page stalling meanwhile
 * (the model and the page share the GPU).
 */
const KEPT_ANCHORS = "kneading-needed.atmosphere-anchors";
/**
 * Scores kept under another version are found again. Raise it when the photos, the
 * questions, the model or a request's clay or variant change.
 */
const ANCHORS_VERSION = 1;

function keptAnchors(): Record<string, [number, number]> {
  try {
    const kept = JSON.parse(localStorage.getItem(KEPT_ANCHORS) ?? "null");
    return kept?.version === ANCHORS_VERSION ? kept.anchors : {};
  } catch {
    return {};
  }
}

function keepAnchors(key: string, found: [number, number]): void {
  try {
    localStorage.setItem(
      KEPT_ANCHORS,
      JSON.stringify({ version: ANCHORS_VERSION, anchors: { ...keptAnchors(), [key]: found } }),
    );
  } catch {
    // No storage (a private window, say): found again next visit.
  }
}

/**
 * How strongly the piece in `photos` gives the air hoped for, as far as it goes from the
 * untouched clay (photographed by `start` when first asked) toward the customer's variant
 * (by `target`): the model's scores are only worth comparing with each other. Those two
 * are kept under `request` (see KEPT_ANCHORS). The model is loaded on first use, reporting
 * how much of it has come by `loading`.
 */
export async function rateAtmosphere(
  request: string,
  atmosphere: Atmosphere,
  photos: ImageData[],
  start: () => ImageData[],
  target: () => ImageData[],
  loading: (share: number) => void,
): Promise<AtmosphereRating> {
  let known = anchors.get(atmosphere);
  if (!known) {
    const key = `${request} ${atmosphere.looksLike.join("|")}`;
    const kept = keptAnchors()[key];
    if (kept) known = Promise.resolve(kept);
    else {
      const [s, t] = [start(), target()];
      known = Promise.all([scoreOf(s, atmosphere, loading), scoreOf(t, atmosphere, loading)]);
      known.then((found) => keepAnchors(key, found), () => anchors.delete(atmosphere));
    }
    anchors.set(atmosphere, known);
  }
  const [score, [from, to]] = await Promise.all([scoreOf(photos, atmosphere, loading), known]);
  const strength = Math.min(1, Math.max(0, (score - from) / Math.max(to - from, MIN_SPAN)));
  const level = strength >= 2 / 3 ? 2 : strength >= 1 / 3 ? 1 : 0;
  return { strength, level, text: LEVELS[level] };
}
